import {
    Button,
    ButtonGroup,
    FormControlLabel,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Popover,
    Radio,
    RadioGroup,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClassifierDto } from "../../models/ClassifierDto";
import ClassifierRecordsLinearView from "./ClassifierRecordsLinearView";
import ClassifierRecordsHierarchicalView from "./ClassifierRecordsHierarchicalView";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import SearchEdit from "../inputs/SearchEdit";
import { Translations } from "../../localization/translations";
import { Dayjs } from "dayjs";
import KlasisDatePicker from "../inputs/KlasisDatePicker";
import { defaultLocale } from "../../constants/languages";
import _ from "lodash";
import SEARCH_PARAMS from "../../constants/searchParams";
import axios, { AxiosError } from "axios";
import { ApiRoutes } from "../../configuration/apiRoutes";
import InfoIcon from "@mui/icons-material/Info";
import { format } from "date-fns";
import SettingsList, { ISettingsListItem } from "../SettingsList";
import { FileType } from "../../models/FileType";
import EnumMapper from "../../utils/enumMapper";
import { LoadingButton } from "@mui/lab";
import fileDownload from "js-file-download";
import useKlasisSnackbar from "../../utils/klasisSnackbar";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useMatomo } from "@jonkoops/matomo-tracker-react";

import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import { ClassifierStructureExistenceInfoRequest } from "../../models/ClassifierStructureExistenceInfoRequest";
import { RestService } from "../../utils/axios";
import { ClassifierStructureExistenceInfoResponse } from "../../models/ClassifierStructureExistenceInfoResponse";
import KlasisLoader from "../KlasisLoader";

enum ViewType {
    Hierarchical = "Hierarchical",
    Linear = "Linear",
}

interface IClassifierMainDataProps {
    data?: ClassifierDto;
    setPassportDataTab: () => void;
}

const ClassifierMainData: React.FC<IClassifierMainDataProps> = (
    props: IClassifierMainDataProps
) => {
    const { id, locale } = useParams();
    const { state } = useLocation();
    const [persistedRequestSuggestionId, setPersistedRequestSuggestionId] = useState<string | null>(null);

    const { t } = useTranslation();
    const [search, setSearch] = useSearchParams();

    const classifierLocale =
        search.get(SEARCH_PARAMS.classifierLocale) ?? locale ?? defaultLocale;
    const searchParamsLevel = parseInt(search.get(SEARCH_PARAMS.level) ?? "1");

    const [view, setView] = useState(ViewType.Hierarchical);
    const [inputSearchPhrase, setInputSearchPhrase] = useState("");
    const [searchPhrase, setSearchPhrase] = useState("");
    const [dateFilterValue, setDateFilterValue] = useState<
        Dayjs | undefined | null
    >(null);
    const [structureExistenceInfo, setStructureExistenceInfo] =
        useState<ClassifierStructureExistenceInfoResponse>();
    const [levels, setLevels] = useState<number[]>([]);

    const level =
        levels.findIndex((item) => item === searchParamsLevel - 1) !== -1
            ? searchParamsLevel
            : 1;

    const getUTC = (date: Date) => {
        const dateObj = new Date(date);
        return new Date(
            Date.UTC(
                dateObj.getFullYear(),
                dateObj.getMonth(),
                dateObj.getDate()
            )
        );
    };

    const getStructureExistenceInfo = () => {
        if (!id || !classifierLocale) return;

        const onDate = dateFilterValue
            ? getUTC(dateFilterValue.toDate())
            : new Date();

        const request: ClassifierStructureExistenceInfoRequest = {
            classifierId: id,
            localeCode: classifierLocale,
            onDate: onDate,
        };

        RestService.Get<ClassifierStructureExistenceInfoResponse>(
            ApiRoutes.Classifiers.GetClassifierStructuresExistenceInfo(request),
            (response: ClassifierStructureExistenceInfoResponse) => {
                setStructureExistenceInfo(response);
            },
            (error: AxiosError) => console.log("Error: " + error.message)
        );
    };

    const getLevels = () => {
        if (!id || !classifierLocale) return;

        const onDate = dateFilterValue
            ? getUTC(dateFilterValue.toDate())
            : undefined;

        RestService.Get<number[]>(
            ApiRoutes.Classifiers.GetClassifierRecordsLevels({
                classifierId: id,
                localeCode: classifierLocale,
                onDate: onDate,
            }),
            (response: number[]) => {
                setLevels(response);
            }
        );
    };

    const getTreeRecordsDateFilter = (): [
        Date | undefined,
        Date | undefined,
        Date | undefined
    ] => {
        if (!structureExistenceInfo) {
            return [undefined, undefined, undefined];
        }

        // if filter changed always show result for selected date
        const dateFilterHasValue = !!dateFilterValue;

        const onDate =
            structureExistenceInfo.hasActiveStructuresOnDate ||
            (dateFilterHasValue && structureExistenceInfo.onDate)
                ? getUTC(structureExistenceInfo.onDate as Date)
                : undefined;
        const validFromDate =
            !structureExistenceInfo.hasActiveStructuresOnDate &&
            structureExistenceInfo.hasStructuresInFuture &&
            !dateFilterHasValue
                ? getUTC(structureExistenceInfo.suitableFromDate as Date)
                : undefined;
        const validToDate =
            !structureExistenceInfo.hasActiveStructuresOnDate &&
            !structureExistenceInfo.hasStructuresInFuture &&
            structureExistenceInfo.hasStructuresInPast &&
            !dateFilterHasValue
                ? getUTC(structureExistenceInfo.suitableToDate as Date)
                : undefined;

        // only one of this dates not null
        return [onDate, validFromDate, validToDate];
    };

    const getSettingsList = () => {
        const gridRows: ISettingsListItem[] = [];
        gridRows.push({
            title: t(Translations.classifier.mainData.view),
            element: (
                <RadioGroup
                    row
                    value={view}
                    onChange={(event: React.ChangeEvent, value: string) => {
                        let newViewType = value as keyof typeof ViewType;
                        if (newViewType === ViewType.Linear) {
                            search.set(SEARCH_PARAMS.level, "1");
                            setSearch(search);
                        }

                        setView(ViewType[newViewType]);
                    }}
                >
                    <FormControlLabel
                        value={ViewType.Hierarchical}
                        control={<Radio size="small" />}
                        label={t(
                            Translations.classifier.mainData.viewHierarchical
                        )}
                    />
                    <FormControlLabel
                        value={ViewType.Linear}
                        control={<Radio size="small" />}
                        label={t(Translations.classifier.mainData.viewLinear)}
                    />
                </RadioGroup>
            ),
        });
        gridRows.push({
            title: t(Translations.classifier.mainData.language),
            element: (
                <ButtonGroup size="small">
                    {props.data?.locales.map((localeItem) => (
                        <Tooltip title={_.toUpper(localeItem.code)}>
                            <Button
                                variant={
                                    localeItem.code === classifierLocale
                                        ? "contained"
                                        : undefined
                                }
                                color={
                                    localeItem.code === classifierLocale
                                        ? "primary"
                                        : undefined
                                }
                                key={`rec-loc-${localeItem.code}`}
                                onClick={() => {
                                    search.set(
                                        SEARCH_PARAMS.classifierLocale,
                                        localeItem.code
                                    );
                                    setSearch(search);
                                }}
                            >
                                {_.toUpper(localeItem.shortCode)}
                            </Button>
                        </Tooltip>
                    ))}
                </ButtonGroup>
            ),
        });
        if (view === ViewType.Hierarchical) {
            gridRows.push({
                title: t(Translations.classifier.mainData.level),
                element: (
                    <ButtonGroup size="small">
                        {levels.map((recordLevel) => (
                            <Button
                                variant={
                                    recordLevel === level - 1
                                        ? "contained"
                                        : undefined
                                }
                                color={
                                    recordLevel === level - 1
                                        ? "primary"
                                        : undefined
                                }
                                key={`lvl-${recordLevel}`}
                                onClick={() => {
                                    search.set(
                                        SEARCH_PARAMS.level,
                                        (recordLevel + 1).toString()
                                    );
                                    setSearch(search);
                                }}
                            >
                                {recordLevel + 1}
                            </Button>
                        ))}
                    </ButtonGroup>
                ),
            });
        }
        gridRows.push({
            title: t(Translations.classifier.mainData.valuesOnDate),
            element: (
                <KlasisDatePicker
                    value={dateFilterValue}
                    label={t(Translations.classifier.mainData.dateInputToday)}
                    onChange={(newValue) => {
                        if (newValue?.isValid() || !newValue)
                            setDateFilterValue(newValue);
                    }}
                />
            ),
        });
        gridRows.push({
            title: t(Translations.classifier.mainData.download),
            element: (
                <ClassifierExport
                    id={id ?? ""}
                    localeCode={locale ?? ""}
                    recordslocaleCode={classifierLocale}
                    getTreeRecordsDateFilter={getTreeRecordsDateFilter}
                    maxLevel={level}
                />
            ),
        });

        return gridRows;
    };

    useEffect(() => {
        if (state && state.requestSuggestionId) {
            const newRequestSuggestionId = state.requestSuggestionId as string;
            if (newRequestSuggestionId !== persistedRequestSuggestionId) {
                setPersistedRequestSuggestionId(newRequestSuggestionId);
            }
        }
    }, [state, persistedRequestSuggestionId]);

    useEffect(() => {
        search.set(SEARCH_PARAMS.level, "1");
        getLevels();
    }, [props.data, classifierLocale, view]);

    useEffect(() => {
        getStructureExistenceInfo();
    }, [id, classifierLocale, dateFilterValue]);

    useEffect(() => {
        if (!structureExistenceInfo) return;

        const hasAnyStructure =
            structureExistenceInfo.hasActiveStructuresNow ||
            structureExistenceInfo.hasActiveStructuresOnDate ||
            structureExistenceInfo.hasStructuresInFuture ||
            structureExistenceInfo.hasStructuresInPast;

        if (!hasAnyStructure) props.setPassportDataTab();
    }, [structureExistenceInfo]);

    const renderSearchResultInfoMessage = () => {
        const dateFilterValueChanged =
            dateFilterValue && !dateFilterValue?.isSame(new Date(), "day");

        if (
            !structureExistenceInfo ||
            (structureExistenceInfo.hasActiveStructuresNow &&
                !dateFilterValueChanged)
        )
            return null;

        let message = "";
        let dateString = "";
        if (dateFilterValueChanged) {
            message = t(Translations.classifier.text.settingPlMsgDateFilter);
            dateString = dateFilterValue
                ? format(
                      dateFilterValue.toDate(),
                      t(Translations.formats.date.text)
                  )
                : "-";
        } else if (
            !dateFilterValue &&
            structureExistenceInfo.hasStructuresInFuture
        ) {
            message = "Attēlotā klasifikācija stāsies spēkā";
            dateString = structureExistenceInfo.suitableFromDate
                ? format(
                      new Date(structureExistenceInfo.suitableFromDate),
                      t(Translations.formats.date.text)
                  )
                : "-";
        } else if (
            !dateFilterValue &&
            structureExistenceInfo.hasStructuresInPast
        ) {
            message = "Attēlotā klasifikācijas versija bija spēkā līdz";
            dateString = structureExistenceInfo.suitableToDate
                ? format(
                      new Date(structureExistenceInfo.suitableToDate),
                      t(Translations.formats.date.text)
                  )
                : "-";
        }

        if (message.length === 0) return null;

        return (
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={(theme) => ({
                    marginTop: 3,
                    color: theme.customComponents.infoBox.accentColor,
                })}
            >
                <InfoIcon />
                <Typography>
                    {message} <b>{dateString}</b>
                </Typography>
            </Stack>
        );
    };

    if (!structureExistenceInfo) return <KlasisLoader />;

    return (
        <>
            <Typography marginBottom={1}>
                {t(Translations.classifier.mainData.viewSettingsTitle)}
            </Typography>

            <SettingsList items={getSettingsList()} />

            {renderSearchResultInfoMessage()}

            <SearchEdit
                placeholder={t(Translations.classifier.mainData.search)}
                value={inputSearchPhrase}
                onSearch={(searchPhrase: string) => {
                    setSearchPhrase(searchPhrase);
                }}
                onChange={(event) => {
                    setInputSearchPhrase(event.target.value);
                }}
                onSeachClear={() => {
                    setSearchPhrase("");
                    setInputSearchPhrase("");
                }}
                sx={{ marginTop: 3 }}
            />
            {view === ViewType.Hierarchical ? (
                <ClassifierRecordsHierarchicalView
                    classifierId={id ?? ""}
                    level={level - 1}
                    classifierLocale={classifierLocale}
                    searchPhrase={searchPhrase}
                    dateFilterValue={dateFilterValue}
                    structuresExistenceInfo={structureExistenceInfo}
                    getTreeRecordsDateFilter={getTreeRecordsDateFilter}
                />
            ) : (
                <ClassifierRecordsLinearView
                    classifierId={id ?? ""}
                    level={level - 1}
                    classifierLocale={classifierLocale}
                    searchPhrase={searchPhrase}
                    dateFilterValue={dateFilterValue}
                    structuresExistenceInfo={structureExistenceInfo}
                    getTreeRecordsDateFilter={getTreeRecordsDateFilter}
                />
            )}
            {persistedRequestSuggestionId && (
                <RateSearch
                    requestSuggestionId={persistedRequestSuggestionId as string}
                />
            )}
        </>
    );
};

interface IRateSearchProps {
    requestSuggestionId: string;
}

const RateSearch: React.FC<IRateSearchProps> = ({ requestSuggestionId }) => {
    const { t } = useTranslation();
    const { klasisEnqueueSnackbar } = useKlasisSnackbar();

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleEvaluate = (isUseful: boolean) => {
        axios.post(ApiRoutes.Search.EvaluateSuggestedRecord(), {
            requestSuggestionId,
            isUseful,
        });

        handleClose();
        klasisEnqueueSnackbar(t(Translations.classifier.text.rateSuccess));
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "rate-popover" : undefined;

    return (
        <Stack justifyContent="flex-end" direction="row">
            <Button
                variant="contained"
                color="info"
                style={{
                    borderRadius: 20,
                }}
                onClick={(event) => {
                    setAnchorEl(event.currentTarget);
                }}
            >
                {t(Translations.classifier.button.rateSearch)}
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
            >
                <Stack
                    sx={(theme) => ({
                        padding: 2,

                        background:
                            theme.customComponents.infoBox.backgroundColor,
                        maxWidth: 360,
                    })}
                >
                    <Typography color="primary">
                        {t(Translations.classifier.text.rateSearchTitle)}
                    </Typography>
                    <Stack direction="row">
                        <Typography sx={{ pt: 2 }}>
                            {t(Translations.classifier.text.rateSearchContent)}
                        </Typography>
                        <Stack justifyContent="space-between">
                            <Tooltip
                                title={t(
                                    Translations.classifier.tooltip
                                        .searchThumbUp
                                )}
                            >
                                <IconButton
                                    color="info"
                                    onClick={() => handleEvaluate(true)}
                                >
                                    <ThumbUpOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip
                                title={t(
                                    Translations.classifier.tooltip
                                        .searchThumbDown
                                )}
                            >
                                <IconButton
                                    color="info"
                                    onClick={() => handleEvaluate(false)}
                                >
                                    <ThumbDownOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </Stack>
            </Popover>
        </Stack>
    );
};

interface IClassifierExportProps {
    id: string;
    localeCode: string;
    recordslocaleCode: string;
    maxLevel?: number;
    getTreeRecordsDateFilter: () => [
        Date | undefined,
        Date | undefined,
        Date | undefined
    ];
}

const ClassifierExport: React.FC<IClassifierExportProps> = ({
    id,
    localeCode,
    recordslocaleCode,
    maxLevel,
    getTreeRecordsDateFilter,
}) => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const { klasisEnqueueSnackbar } = useKlasisSnackbar();
    const { trackLink } = useMatomo();

    const [isExporting, setIsExporting] = useState(false);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDownload = (fileType: FileType) => {
        handleClose();

        const [onDate, validFromDate, validToDate] = getTreeRecordsDateFilter();

        setIsExporting(true);

        if (state && state.requestSuggestionId) {
            axios.post(ApiRoutes.Search.EvaluateRecordDownload(), {
                requestSuggestionId: state.requestSuggestionId,
            });
        }

        const fileUrl = ApiRoutes.Classifiers.DownloadRecords({
            id,
            localeCode,
            recordsLocaleCode: recordslocaleCode,
            fileType: fileType,
            maxLevel,
            // validFromDate == validToDate => filter records that will be active during that date will be selected
            validFromDate: onDate ? onDate : validFromDate,
            validToDate: onDate ? onDate : validToDate,
        });

        axios
            .get(fileUrl, {
                responseType: "blob",
            })
            .then((response) => {
                if (!response) return null;

                const contentDisposition =
                    response.headers["content-disposition"];
                const matches = /filename=(.*);/.exec(contentDisposition);

                const fileName = matches && matches[1] ? matches[1] : "unknown";

                fileDownload(response.data, fileName);

                klasisEnqueueSnackbar(
                    t(Translations.classifier.text.downloadSuccess)
                );

                trackLink({
                    linkType: "download",
                    href: fileUrl,
                });
            })
            .catch((error) => {})
            .finally(() => {
                setIsExporting(false);
            });
    };

    return (
        <Stack>
            <div>
                <LoadingButton
                    loading={isExporting}
                    id="download-button"
                    aria-controls={open ? "download-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="contained"
                    color="primary"
                    size="small"
                    endIcon={
                        <ArrowDropDownIcon
                            style={{
                                transform: open ? "rotate(180deg)" : undefined,
                                transition: "transform 0.2s",
                            }}
                        />
                    }
                >
                    {t(Translations.classifier.mainData.download)}
                </LoadingButton>
            </div>
            <Menu
                id="download-menu"
                aria-labelledby="download-button"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                {Object.values(FileType)
                    .filter((x) => Number(x))
                    .map((variant) => (
                        <MenuItem
                            key={variant}
                            value={variant}
                            onClick={() => handleDownload(Number(variant))}
                        >
                            <ListItemIcon>
                                <AttachFileIcon
                                    fontSize="small"
                                    color="primary"
                                />
                            </ListItemIcon>
                            <ListItemText
                                primaryTypographyProps={{
                                    color: "primary",
                                }}
                            >
                                {t(
                                    EnumMapper.getI18Key(
                                        "filetype",
                                        Number(variant)
                                    )
                                )}
                            </ListItemText>
                        </MenuItem>
                    ))}
            </Menu>
        </Stack>
    );
};

export default ClassifierMainData;
